import type { StorageDriver, StorageDriverCapability, StorageDriverType, StorageEntry, StorageMode, StorageQuota, StorageWriteOptions, StorageWriteResult, ShareUrlOptions, EncryptedEnvelope } from '../types';
import { encryptEnvelope, decryptEnvelope } from '../crypto';

export interface GDriveDriverConfig {
  /** OAuth 2.0 Access Token or dynamic token getter */
  accessToken: string | (() => Promise<string>);
  /** Name of the root folder in Google Drive (default: 'YADA') */
  rootFolderName?: string;
  /** Storage mode: encrypted-vault (default) or open-interop */
  mode?: StorageMode;
  /** Passphrase or CryptoKey used when mode is 'encrypted-vault' */
  encryptionKey?: CryptoKey | string;
}

export class GDriveStorageDriver implements StorageDriver {
  readonly id: string;
  readonly type: StorageDriverType = 'gdrive';
  readonly capabilities: ReadonlySet<StorageDriverCapability> = new Set([
    'read',
    'write',
    'delete',
    'list',
    'share',
    'direct-download',
    'quota',
  ]);

  private readonly getAccessToken: () => Promise<string>;
  private readonly rootFolderName: string;
  private defaultMode: StorageMode;
  private defaultEncryptionKey?: CryptoKey | string;

  // Folder ID cache to avoid repeated search queries
  private rootFolderIdCache: string | null = null;
  private folderIdCache = new Map<string, string>();

  constructor(config: GDriveDriverConfig) {
    this.id = 'gdrive-driver';
    this.rootFolderName = config.rootFolderName || 'YADA';
    this.defaultMode = config.mode || 'encrypted-vault';
    this.defaultEncryptionKey = config.encryptionKey;

    if (typeof config.accessToken === 'function') {
      this.getAccessToken = config.accessToken;
    } else {
      const token = config.accessToken;
      this.getAccessToken = async () => token;
    }
  }

  private async fetchAuth(url: string, init?: RequestInit): Promise<Response> {
    const token = await this.getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, { ...init, headers });
  }

  /**
   * Ensures the root YADA directory exists in the user's Drive and returns its ID
   */
  private async getRootFolderId(): Promise<string> {
    if (this.rootFolderIdCache) return this.rootFolderIdCache;

    const query = `name = '${this.rootFolderName}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
    const res = await this.fetchAuth(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=drive&fields=files(id,name)`
    );

    if (!res.ok) {
      throw new Error(`Failed to query root folder in Google Drive: ${res.statusText}`);
    }

    const data = await res.json();
    if (data.files && data.files.length > 0) {
      this.rootFolderIdCache = data.files[0].id;
      return this.rootFolderIdCache!;
    }

    // Create root folder
    const createRes = await this.fetchAuth('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: this.rootFolderName,
        mimeType: 'application/vnd.google-apps.folder',
      }),
    });

    if (!createRes.ok) {
      throw new Error(`Failed to create root folder '${this.rootFolderName}' in Google Drive: ${createRes.statusText}`);
    }

    const created = await createRes.json();
    this.rootFolderIdCache = created.id;
    return this.rootFolderIdCache!;
  }

  /**
   * Resolves or creates nested folders for a given path under the root folder
   */
  private async resolveParentFolderId(path: string): Promise<{ folderId: string; fileName: string }> {
    const segments = path.replace(/^\/+|\/+$/g, '').split('/');
    const fileName = segments.pop()!;
    let currentFolderId = await this.getRootFolderId();

    let accumulatedPath = '';
    for (const segment of segments) {
      accumulatedPath += '/' + segment;
      if (this.folderIdCache.has(accumulatedPath)) {
        currentFolderId = this.folderIdCache.get(accumulatedPath)!;
        continue;
      }

      const query = `name = '${segment}' and '${currentFolderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
      const res = await this.fetchAuth(
        `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`
      );

      const data = await res.json();
      if (data.files && data.files.length > 0) {
        currentFolderId = data.files[0].id;
      } else {
        // Create subfolder
        const createRes = await this.fetchAuth('https://www.googleapis.com/drive/v3/files', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: segment,
            parents: [currentFolderId],
            mimeType: 'application/vnd.google-apps.folder',
          }),
        });
        const created = await createRes.json();
        currentFolderId = created.id;
      }
      this.folderIdCache.set(accumulatedPath, currentFolderId);
    }

    return { folderId: currentFolderId, fileName };
  }

  /**
   * Finds a file in Google Drive by path under the YADA folder
   */
  private async findFile(path: string): Promise<{ id: string; name: string; size?: string; modifiedTime?: string } | null> {
    const { folderId, fileName } = await this.resolveParentFolderId(path);
    const query = `name = '${fileName}' and '${folderId}' in parents and trashed = false`;
    const res = await this.fetchAuth(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,size,modifiedTime)`
    );

    if (!res.ok) return null;
    const data = await res.json();
    if (data.files && data.files.length > 0) {
      return data.files[0];
    }
    return null;
  }

  async read(path: string): Promise<Uint8Array | null> {
    const file = await this.findFile(path);
    if (!file) return null;

    const res = await this.fetchAuth(`https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(`Failed to read file '${path}' from Google Drive: ${res.statusText}`);
    }

    const rawBytes = new Uint8Array(await res.arrayBuffer());

    const envelope = parseEncryptedEnvelope(rawBytes);
    if (envelope) {
      const key = this.defaultEncryptionKey;
      if (!key) {
        throw new Error(`File '${path}' is encrypted, but no encryption key was configured on the driver.`);
      }
      // A decryption failure is an error, never a reason to hand back the ciphertext.
      return await decryptEnvelope(envelope, key);
    }
    if (this.defaultMode === 'encrypted-vault') {
      throw new Error(`File '${path}' is not an encrypted envelope; refusing to read it as plaintext in encrypted-vault mode.`);
    }
    return rawBytes;
  }

  async write(path: string, data: Uint8Array, options?: StorageWriteOptions): Promise<StorageWriteResult> {
    const mode = options?.mode ?? this.defaultMode;
    let payloadToUpload = data;
    let contentType = options?.contentType || 'application/octet-stream';

    // Apply client-side envelope encryption if in encrypted-vault mode
    if (mode === 'encrypted-vault') {
      const key = options?.encryptionKey ?? this.defaultEncryptionKey;
      if (!key) {
        throw new Error(`Cannot write '${path}' in encrypted-vault mode without an encryption key.`);
      }
      const envelope = await encryptEnvelope(data, key, options?.keyId ?? 'primary');
      const envelopeJson = JSON.stringify(envelope);
      payloadToUpload = new TextEncoder().encode(envelopeJson);
      contentType = 'application/json';
    }

    const { folderId, fileName } = await this.resolveParentFolderId(path);
    const existingFile = await this.findFile(path);

    const metadata = {
      name: fileName,
      parents: existingFile ? undefined : [folderId],
      properties: options?.customMetadata,
    };

    const boundary = '-------314159265358979323846';
    const delimiter = `\r\n--${boundary}\r\n`;
    const closeDelimiter = `\r\n--${boundary}--`;

    const metadataPart = `${delimiter}Content-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}`;
    const mediaHeader = `${delimiter}Content-Type: ${contentType}\r\n\r\n`;

    const metadataBytes = new TextEncoder().encode(metadataPart);
    const mediaHeaderBytes = new TextEncoder().encode(mediaHeader);
    const closeDelimiterBytes = new TextEncoder().encode(closeDelimiter);

    const totalLength = metadataBytes.length + mediaHeaderBytes.length + payloadToUpload.length + closeDelimiterBytes.length;
    const bodyBytes = new Uint8Array(totalLength);

    let offset = 0;
    bodyBytes.set(metadataBytes, offset); offset += metadataBytes.length;
    bodyBytes.set(mediaHeaderBytes, offset); offset += mediaHeaderBytes.length;
    bodyBytes.set(payloadToUpload, offset); offset += payloadToUpload.length;
    bodyBytes.set(closeDelimiterBytes, offset);

    let url: string;
    let method: string;
    if (existingFile) {
      url = `https://www.googleapis.com/upload/drive/v3/files/${existingFile.id}?uploadType=multipart&fields=id,modifiedTime,size`;
      method = 'PATCH';
    } else {
      url = `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,modifiedTime,size`;
      method = 'POST';
    }

    const res = await this.fetchAuth(url, {
      method,
      headers: {
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: bodyBytes,
    });

    if (!res.ok) {
      throw new Error(`Failed to write file '${path}' to Google Drive: ${res.statusText}`);
    }

    const result = await res.json();
    return {
      path,
      sizeBytes: payloadToUpload.byteLength,
      updatedAt: result.modifiedTime || new Date().toISOString(),
      versionId: result.id,
    };
  }

  async delete(path: string): Promise<boolean> {
    const file = await this.findFile(path);
    if (!file) return false;

    const res = await this.fetchAuth(`https://www.googleapis.com/drive/v3/files/${file.id}`, {
      method: 'DELETE',
    });

    return res.ok || res.status === 404;
  }

  async list(prefix?: string): Promise<StorageEntry[]> {
    const rootId = await this.getRootFolderId();
    const query = `'${rootId}' in parents and trashed = false`;
    const res = await this.fetchAuth(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType,size,modifiedTime)`
    );

    if (!res.ok) {
      throw new Error(`Failed to list Google Drive directory: ${res.statusText}`);
    }

    const data = await res.json();
    const entries: StorageEntry[] = [];

    for (const f of data.files || []) {
      const isDir = f.mimeType === 'application/vnd.google-apps.folder';
      if (!prefix || f.name.startsWith(prefix)) {
        entries.push({
          name: f.name,
          path: `/${f.name}`,
          isDirectory: isDir,
          sizeBytes: f.size ? parseInt(f.size, 10) : undefined,
          updatedAt: f.modifiedTime,
        });
      }
    }

    return entries;
  }

  async getShareableUrl(path: string, options?: ShareUrlOptions): Promise<string> {
    const file = await this.findFile(path);
    if (!file) {
      throw new Error(`File '${path}' not found in Google Drive`);
    }

    if (options?.allowPublicLink !== false) {
      await this.fetchAuth(`https://www.googleapis.com/drive/v3/files/${file.id}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: options?.role || 'reader',
          type: 'anyone',
        }),
      });
    }

    const res = await this.fetchAuth(`https://www.googleapis.com/drive/v3/files/${file.id}?fields=webContentLink,webViewLink`);
    const fileData = await res.json();
    return fileData.webContentLink || fileData.webViewLink || `https://drive.google.com/file/d/${file.id}/view`;
  }

  async getQuota(): Promise<StorageQuota | null> {
    const res = await this.fetchAuth('https://www.googleapis.com/drive/v3/about?fields=storageQuota');
    if (!res.ok) return null;

    const data = await res.json();
    const q = data.storageQuota;
    if (!q) return null;

    const totalBytes = q.limit ? parseInt(q.limit, 10) : Number.MAX_SAFE_INTEGER;
    const usedBytes = q.usage ? parseInt(q.usage, 10) : 0;
    return {
      totalBytes,
      usedBytes,
      availableBytes: Math.max(0, totalBytes - usedBytes),
    };
  }
}

function parseEncryptedEnvelope(bytes: Uint8Array): EncryptedEnvelope | null {
  const text = new TextDecoder().decode(bytes);
  if (!text.startsWith('{') || !text.includes('"crypto_version":1')) return null;
  try {
    return JSON.parse(text) as EncryptedEnvelope;
  } catch {
    return null;
  }
}
